import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Mail, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";
import { SOCIAL, TAGLINE } from "@/lib/social";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter({ email });
      toast.success("Subscribed — ngā mihi.");
      setEmail("");
    } catch {
      toast.error("Couldn't subscribe right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-ink text-white relative overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 grain-overlay opacity-[0.04] pointer-events-none" />
      <div className="container-x relative pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <img src="/logo.jpg" alt="Aotūroa Academy logo" className="h-12 w-auto rounded-md bg-white p-1" />
              <span className="font-heading font-semibold leading-tight">Aoturoa Academy Charitable Trust</span>
            </div>
            <p className="text-white/70 max-w-md leading-relaxed">
              Sexual health and wellbeing education for rangatahi — and workshops for the educators, professionals and
              organisations who support them across Aotearoa.
            </p>
            <p className="mt-5 italic text-crimson/90 font-heading text-base">"{TAGLINE}"</p>
            <p className="mt-5 text-xs text-white/45 leading-relaxed">
              Aoturoa Academy Charitable Trust · Registered Charity CC58012<br />
              Founded by Shailesh Bagwe · Tāmaki Makaurau, Auckland
            </p>

            <form onSubmit={onSubscribe} className="mt-8 max-w-md" data-testid="footer-newsletter-form">
              <label className="label-eyebrow !text-crimson mb-3 block">Newsletter</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="footer-newsletter-input"
                  className="flex-1 bg-white/10 border border-white/20 rounded-full px-5 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-crimson"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="footer-newsletter-submit"
                  className="btn-crimson !py-3"
                >
                  {loading ? "..." : <ArrowRight className="w-4 h-4" />}
                </button>
              </div>
            </form>
          </div>

          <div className="md:col-span-3 md:col-start-7">
            <h4 className="font-heading font-semibold mb-5 text-white">Explore</h4>
            <ul className="space-y-3 text-white/70">
              <li><Link to="/about" className="hover:text-crimson transition-colors">About</Link></li>
              <li><Link to="/workshops" className="hover:text-crimson transition-colors">Workshops</Link></li>
              <li><Link to="/programmes" className="hover:text-crimson transition-colors">Our Programme</Link></li>
              <li><Link to="/leadership" className="hover:text-crimson transition-colors">Leadership &amp; Governance</Link></li>
              <li><Link to="/donate" className="hover:text-crimson transition-colors">Donate</Link></li>
              <li><Link to="/contact" className="hover:text-crimson transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-heading font-semibold mb-5 text-white">Connect</h4>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 mt-1 text-crimson flex-shrink-0" />
                <a href="mailto:alex.amboy@aoturoaacademy.org.nz" className="hover:text-crimson break-all">
                  alex.amboy@aoturoaacademy.org.nz
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-1 text-crimson flex-shrink-0" />
                <span>Tāmaki Makaurau, Auckland</span>
              </li>
            </ul>
            <div className="flex items-center gap-3 mt-6">
              <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram @aoturoa.academy.nz" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-crimson hover:border-crimson transition-all">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" aria-label="Facebook Aotūroa Academy" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-crimson hover:border-crimson transition-all">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={SOCIAL.linkedin} data-testid="social-linkedin" aria-label="LinkedIn" className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-crimson hover:border-crimson transition-all">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm text-white/50">
          <p>© {new Date().getFullYear()} Aoturoa Academy Charitable Trust (CC58012). All rights reserved.</p>
          <p>He waka eke noa — we are all in this together.</p>
        </div>
      </div>
    </footer>
  );
}
